// src/components/gallery/CharacterGalleryUpload.tsx
/**
 * キャラクターギャラリー画像アップロードモーダル
 * ドラッグ&ドロップでXOR-Pix画像をアップロード
 * タイトル・説明入力対応
 */
import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { uploadCharacterGalleryImage, getGalleryUploadStatus } from '../../services/characterService';

interface CharacterGalleryUploadProps {
  characterId: string;
  onUploadComplete?: (modelId: string) => void;
  onClose?: () => void;
}

type UploadStage = 'idle' | 'encrypting' | 'uploading' | 'processing' | 'completed' | 'error';

const CharacterGalleryUpload: React.FC<CharacterGalleryUploadProps> = ({
  characterId,
  onUploadComplete,
  onClose,
}) => {
  const { t } = useTranslation('character');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [stage, setStage] = useState<UploadStage>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [showOnFigryneTop, setShowOnFigryneTop] = useState(true);

  // ファイル選択処理
  const handleFileSelect = useCallback((file: File) => {
    // 画像ファイルのみ許可
    if (!file.type.startsWith('image/')) {
      setError(t('gallery.uploadModal.invalidType'));
      return;
    }

    // ファイルサイズ制限 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError(t('gallery.uploadModal.tooLarge'));
      return;
    }

    setError(null);
    setSelectedFile(file);

    // プレビュー生成
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, [t]);

  // ドラッグ&ドロップハンドラー
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // ファイル入力ハンドラー
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // ステータスポーリング
  const pollStatus = useCallback(async (taskId: string): Promise<string | null> => {
    const maxAttempts = 60; // 最大60回 (約5分)
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const status = await getGalleryUploadStatus(taskId);

        if (status.status === 'completed') {
          return status.model_id || taskId;
        }

        if (status.status === 'failed') {
          throw new Error(status.error || 'Upload failed');
        }

        // 進捗更新
        if (status.progress > 50) {
          setProgress(70 + (status.progress - 50) * 0.6);  // 70-100%
        }

        // 待機
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
      } catch (err) {
        throw err;
      }
    }

    throw new Error('Upload timed out');
  }, []);

  // アップロード実行
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setStage('encrypting');
    setProgress(0);
    setError(null);

    try {
      // 1. XOR暗号化 & アップロード (常にillustrationとして保存)
      const taskId = await uploadCharacterGalleryImage(
        characterId,
        selectedFile,
        {
          mediaType: 'illustration',
          title: title.trim() || undefined,
          description: description.trim() || undefined,
          showOnFigryneTop,
        },
        (prog, stg) => {
          setProgress(prog);
          if (stg === 'encrypting') setStage('encrypting');
          else if (stg === 'uploading') setStage('uploading');
          else if (stg === 'processing') setStage('processing');
        }
      );

      // 2. ステータスポーリング
      setStage('processing');
      const modelId = await pollStatus(taskId);

      // 3. 完了
      setStage('completed');
      setProgress(100);

      // 1.5秒後にコールバック
      setTimeout(() => {
        onUploadComplete?.(modelId!);
      }, 1500);

    } catch (err) {
      console.error('Upload error:', err);
      setStage('error');
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  }, [selectedFile, characterId, title, description, showOnFigryneTop, pollStatus, onUploadComplete]);

  // リセット
  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setStage('idle');
    setProgress(0);
    setError(null);
    setTitle('');
    setDescription('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // ステージに応じたメッセージ
  const getStageMessage = (): string => {
    switch (stage) {
      case 'encrypting':
        return t('gallery.uploadModal.stage.encrypting');
      case 'uploading':
        return t('gallery.uploadModal.stage.uploading');
      case 'processing':
        return t('gallery.uploadModal.stage.processing');
      case 'completed':
        return t('gallery.uploadModal.stage.completed');
      case 'error':
        return error || t('gallery.uploadModal.stage.error');
      default:
        return '';
    }
  };

  const isUploading = stage !== 'idle' && stage !== 'completed' && stage !== 'error';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {t('gallery.uploadModal.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            disabled={isUploading}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* コンテンツ */}
        <div className="p-5 space-y-4">
          {/* ファイル選択エリア */}
          {!selectedFile && stage === 'idle' && (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleInputChange}
                className="hidden"
              />
              <CloudArrowUpIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">
                {t('gallery.uploadModal.dropzone')}
              </p>
              <p className="text-gray-400 dark:text-gray-500 text-xs">
                {t('gallery.uploadModal.formats')}
              </p>
            </div>
          )}

          {/* プレビュー */}
          {selectedFile && previewUrl && (
            <div className="space-y-4">
              <div className="relative aspect-video bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
                {/* 進捗オーバーレイ */}
                {isUploading && (
                  <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                    <Loader2 className="w-8 h-8 text-white animate-spin mb-2" />
                    <p className="text-white text-sm">{getStageMessage()}</p>
                    <div className="w-48 h-2 bg-white/30 rounded-full mt-3 overflow-hidden">
                      <div
                        className="h-full bg-[#CCFF00] transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-white/80 text-xs mt-1">{Math.round(progress)}%</p>
                  </div>
                )}
                {/* 完了オーバーレイ */}
                {stage === 'completed' && (
                  <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                    <CheckCircle className="w-12 h-12 text-[#CCFF00] mb-2" />
                    <p className="text-white text-sm">{getStageMessage()}</p>
                  </div>
                )}
                {/* エラーオーバーレイ */}
                {stage === 'error' && (
                  <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center px-4">
                    <AlertCircle className="w-12 h-12 text-red-500 mb-2" />
                    <p className="text-white text-sm text-center">{getStageMessage()}</p>
                  </div>
                )}
              </div>

              {/* タイトル・説明入力 (idle時のみ) */}
              {stage === 'idle' && (
                <>
                  {/* タイトル入力 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('gallery.uploadModal.titleLabel')}
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      maxLength={50}
                      placeholder={t('gallery.uploadModal.titlePlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="mt-1 text-xs text-gray-400 text-right">{title.length}/50</p>
                  </div>

                  {/* 説明入力 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('gallery.uploadModal.descriptionLabel')}
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={500}
                      rows={3}
                      placeholder={t('gallery.uploadModal.descriptionPlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                    <p className="mt-1 text-xs text-gray-400 text-right">{description.length}/500</p>
                  </div>

                  {/* FIGRYNEトップに載せる */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showOnFigryneTop}
                      onChange={(e) => setShowOnFigryneTop(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t('upload.showOnFigryneTop')}
                    </span>
                  </label>
                </>
              )}
            </div>
          )}

          {/* エラーメッセージ (ファイル選択時) */}
          {error && stage === 'idle' && (
            <p className="text-red-500 text-sm text-center">{error}</p>
          )}
        </div>

        {/* フッター */}
        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
          {stage === 'idle' && (
            <>
              {selectedFile ? (
                <>
                  <button
                    onClick={handleReset}
                    className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t('actions.cancel')}
                  </button>
                  <button
                    onClick={handleUpload}
                    className="flex-1 py-2.5 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600 transition-colors"
                  >
                    {t('gallery.uploadModal.button')}
                  </button>
                </>
              ) : (
                <button
                  onClick={onClose}
                  className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {t('actions.close')}
                </button>
              )}
            </>
          )}
          {(stage === 'completed' || stage === 'error') && (
            <button
              onClick={stage === 'completed' ? onClose : handleReset}
              className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {stage === 'completed' ? t('actions.close') : t('actions.retry')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CharacterGalleryUpload;
